// 能源畫面的小元件：比較（▲▼ + 朗讀）、「—」的原因（可聚焦，WCAG 1.4.13）、錯誤三段（說明 · 重試 · 詳細）、mock 提示條。
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isKinioError } from '@kinio/web-sdk';
import { energyMode } from './api';
import type { Fmt } from './format';
import { noCompareText } from './model';
import type { NoCompareReason } from './types';
import './energy.css';
import '../kmgr/kmgr.css';

/** 錯誤碼 → 一句話。 */
export function energyErrText(t: (k: string, o?: Record<string, unknown>) => string, e: unknown): string {
  if (isKinioError(e)) {
    const reason = typeof e.detail?.reason === 'string' ? e.detail.reason : e.message;
    return t(`energy:err.${e.code}`, { defaultValue: t('energy:err.other', { code: e.code }), reason });
  }
  return t('energy:err.other', { code: String((e as Error)?.message ?? e) });
}

export function errCode(e: unknown): string {
  return isKinioError(e) ? e.code : 'NETWORK';
}

/** 比較：▲ 12%（朗讀「多 12%」），不符條件寫「—」並可聚焦看原因。 */
export function Cmp({
  fmt,
  cmp,
  cur,
  reason,
  prevCoverage,
}: {
  fmt: Fmt;
  cmp: { ratio: number | null; prev_mwh: number } | null;
  cur: number;
  reason?: NoCompareReason;
  prevCoverage?: number;
}) {
  const { t } = useTranslation();
  if (!cmp) return <Why text={noCompareText(t, reason, prevCoverage)} />;
  const c = fmt.compare(cmp.ratio, cmp.prev_mwh, cur);
  return (
    <span className="en-cmp nw" aria-label={c.aria}>
      {c.text}
    </span>
  );
}

/** 「—」：按鈕（點按、鍵盤焦點、滑過都拿得到原因）。 */
export function Why({ text }: { text: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <span className="en-pop-anchor">
      <button
        type="button"
        className="km-why"
        aria-label={`— ${t('energy:cmp.why_btn')}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        data-testid="en-why"
      >
        —
      </button>
      {open && (
        <span className="km-pop" role="tooltip">
          {text}
        </span>
      )}
    </span>
  );
}

/** 讀不到：「讀不到用電資料（原因）〔重試〕」+ 錯誤碼收在〔詳細〕。 */
export function LoadError({ error, retry, testId }: { error: unknown; retry?: () => void; testId?: string }) {
  const { t } = useTranslation();
  const code = errCode(error);
  return (
    <div className="km-bar bad" role="alert" data-testid={testId ?? 'en-error'}>
      <span className="ic" aria-hidden>
        ⊘
      </span>
      <span className="txt">
        {t('energy:load_err', { reason: energyErrText(t, error) })}
        <details className="small">
          <summary>{t('energy:err.details')}</summary>
          <code>{code}</code>{' '}
          <button type="button" className="btn btn-sm" onClick={() => void navigator.clipboard?.writeText(code)}>
            {t('energy:err.copy')}
          </button>
        </details>
      </span>
      {retry && (
        <button type="button" className="btn btn-sm" onClick={retry}>
          {t('energy:retry')}
        </button>
      )}
    </div>
  );
}

/** dev / test 用假資料時的提示條（prod 不會出現）。 */
export function MockBanner() {
  const { t } = useTranslation();
  if (energyMode() === 'off') return null;
  return (
    <div className="km-bar" role="note" data-testid="en-mock">
      <span className="ic" aria-hidden>
        ⓘ
      </span>
      <span className="txt small">{t('energy:mock_banner')}</span>
    </div>
  );
}

/** 單位字旁的「1 度 = 1 kWh」（只在寫「度」時第一次出現）。 */
export function UnitHint({ fmt }: { fmt: Fmt }) {
  const { t } = useTranslation();
  if (fmt.unit !== 'du' || fmt.unitText === 'kWh') return null;
  return <span className="en-note"> （{t('energy:unit.du_hint')}）</span>;
}

/** 每列各自儲存的列（UX-06）。 */
export function SetRow({ label, desc, children, act }: { label: string; desc?: string; children: ReactNode; act?: ReactNode }) {
  return (
    <div className="en-setrow">
      <div>
        <div className="lab">{label}</div>
        {desc && <div className="en-note">{desc}</div>}
      </div>
      <div className="val">{children}</div>
      <div className="act">{act}</div>
    </div>
  );
}
